import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Car, Ship } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { galleryImages } from '../data/mock';

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTab, setCurrentTab] = useState('carros');

  const openLightbox = (image, index, tab) => {
    setSelectedImage(image);
    setCurrentIndex(index);
    setCurrentTab(tab);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const images = galleryImages[currentTab];
    const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const goToNext = () => {
    const images = galleryImages[currentTab];
    const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  return (
    <section id="gallery" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2 block">
            Galeria de Trabalhos
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            Nossos Trabalhos Realizados
          </h2>
          <p className="text-lg text-slate-600">
            Veja a qualidade e perfeição dos nossos serviços em cada projeto concluído
          </p>
        </div>

        {/* Tabs Navigation */}
        <Tabs defaultValue="carros" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 h-14 bg-slate-100">
            <TabsTrigger 
              value="carros" 
              className="text-base font-semibold data-[state=active]:bg-blue-600 data-[state=active]:text-white flex items-center gap-2"
            >
              <Car size={20} />
              Carros ({galleryImages.carros.length})
            </TabsTrigger>
            <TabsTrigger 
              value="embarcacoes" 
              className="text-base font-semibold data-[state=active]:bg-blue-600 data-[state=active]:text-white flex items-center gap-2"
            >
              <Ship size={20} />
              Embarcações ({galleryImages.embarcacoes.length})
            </TabsTrigger>
          </TabsList>

          {/* Carros Gallery */}
          <TabsContent value="carros">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {galleryImages.carros.map((image, index) => (
                <div 
                  key={image.id}
                  className="group relative aspect-square overflow-hidden rounded-xl shadow-lg cursor-pointer bg-slate-200 hover:shadow-2xl transition-all duration-300"
                  onClick={() => openLightbox(image, index, 'carros')}
                >
                  <img 
                    src={image.url}
                    alt={`Carro LMP Pinturas ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Embarcações Gallery */}
          <TabsContent value="embarcacoes">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {galleryImages.embarcacoes.map((image, index) => (
                <div 
                  key={image.id}
                  className="group relative aspect-square overflow-hidden rounded-xl shadow-lg cursor-pointer bg-slate-200 hover:shadow-2xl transition-all duration-300"
                  onClick={() => openLightbox(image, index, 'embarcacoes')}
                >
                  <img 
                    src={image.url}
                    alt={`Embarcação LMP Pinturas ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Call to Action - Facebook */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 md:p-12 text-center text-white shadow-2xl relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
            
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Quer Ver Mais?
              </h3>
              <p className="text-lg md:text-xl text-blue-100 mb-6 leading-relaxed">
                Se quiser ver mais imagens e vídeos de trabalhos realizados pela nossa equipa, 
                basta clicar no ícone do <strong className="text-white">Facebook</strong> no fim da página 
                para ser redirecionado para a nossa página oficial.
              </p>
              <div className="flex items-center justify-center gap-2 text-blue-100">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="font-semibold">Facebook · LMP Pinturas</span>
              </div>
            </div>
          </div>
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-blue-400 transition-colors p-2 hover:bg-white/10 rounded-full z-10"
            >
              <X size={32} />
            </button>

            {/* Previous Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-4 text-white hover:text-blue-400 transition-colors p-2 hover:bg-white/10 rounded-full z-10"
            >
              <ChevronLeft size={40} />
            </button>

            {/* Image */}
            <div 
              className="max-w-6xl max-h-[90vh] relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={selectedImage.url}
                alt={`LMP Pinturas ${currentIndex + 1}`}
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
              />
              <div className="text-center mt-4">
                <p className="text-slate-300 text-sm">
                  {currentIndex + 1} / {galleryImages[currentTab].length}
                </p>
              </div>
            </div>

            {/* Next Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-4 text-white hover:text-blue-400 transition-colors p-2 hover:bg-white/10 rounded-full z-10"
            >
              <ChevronRight size={40} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
